import { create } from 'zustand';
import type {
  ChatMessage,
  Conversation,
  ChatAttachment,
  GuidedFlowId,
  ChatEventLog,
} from '@/services/ai/types';
import { chatEngine } from '@/services/ai/chatEngine';

const MAX_MESSAGES_PER_CONVERSATION = 100;
const MAX_CONVERSATIONS = 10;
const MAX_INPUT_LENGTH = 4000;

export interface ChatStoreState {
  conversations: Conversation[];
  activeConversationId: string | null;
  isOpen: boolean;
  isMinimized: boolean;
  isStreaming: boolean;
  mode: 'support' | 'analysis';
  activeGuidedFlow: GuidedFlowId | null;
  activeGuidedFlowStepIndex: number;
  currentRoute: string;
  pendingErrorContext: ChatAttachment | null;
  eventLog: ChatEventLog[];

  // Actions
  sendMessage: (content: string) => Promise<void>;
  open: () => void;
  close: () => void;
  minimize: () => void;
  maximize: () => void;
  clearConversation: () => void;
  newConversation: () => void;
  setMode: (mode: 'support' | 'analysis') => void;
  setRoute: (route: string) => void;
  startGuidedFlow: (flowId: GuidedFlowId) => void;
  cancelGuidedFlow: () => void;
  injectErrorContext: (context: ChatAttachment) => void;
}

function getOrCreateConversation(state: ChatStoreState): {
  conversation: Conversation;
  conversations: Conversation[];
  activeConversationId: string;
} {
  const existing = state.conversations.find(c => c.id === state.activeConversationId);
  if (existing) {
    return {
      conversation: existing,
      conversations: state.conversations,
      activeConversationId: existing.id,
    };
  }

  const newConv: Conversation = {
    id: `conv_${Date.now()}`,
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    mode: state.mode,
  };

  return {
    conversation: newConv,
    conversations: [newConv, ...state.conversations].slice(0, MAX_CONVERSATIONS),
    activeConversationId: newConv.id,
  };
}

export const useChatStore = create<ChatStoreState>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  isOpen: false,
  isMinimized: false,
  isStreaming: false,
  mode: 'support',
  activeGuidedFlow: null,
  activeGuidedFlowStepIndex: 0,
  currentRoute: '/overview',
  pendingErrorContext: null,
  eventLog: [],

  sendMessage: async (content: string) => {
    const state = get();
    if (state.isStreaming) return;
    if (!content.trim()) return;

    const trimmedContent = content.slice(0, MAX_INPUT_LENGTH);

    set({ isStreaming: true });

    // Ensure active conversation
    const { conversation, conversations, activeConversationId } = getOrCreateConversation(state);

    // Create user message
    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: trimmedContent,
      timestamp: new Date().toISOString(),
      status: 'complete',
      attachments: state.pendingErrorContext ? [state.pendingErrorContext] : undefined,
    };

    // Create streaming placeholder
    const placeholderId = `asst_${Date.now()}`;
    const placeholder: ChatMessage = {
      id: placeholderId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      status: 'streaming',
    };

    // Update state with user message + placeholder
    const updatedConv: Conversation = {
      ...conversation,
      messages: [...conversation.messages, userMsg, placeholder].slice(-MAX_MESSAGES_PER_CONVERSATION),
      updatedAt: new Date().toISOString(),
      title: conversation.title ?? trimmedContent.slice(0, 50),
    };

    const updatedConversations = conversations.map(c =>
      c.id === activeConversationId ? updatedConv : c,
    );

    // If conversation was new and not in list yet, add it
    const finalConversations = updatedConversations.some(c => c.id === activeConversationId)
      ? updatedConversations
      : [updatedConv, ...updatedConversations].slice(0, MAX_CONVERSATIONS);

    set({
      conversations: finalConversations,
      activeConversationId,
      pendingErrorContext: null,
    });

    try {
      const result = await chatEngine.processMessage(trimmedContent, updatedConv, {
        onChunk: (chunk) => {
          set(s => ({
            conversations: s.conversations.map(c =>
              c.id === activeConversationId
                ? {
                    ...c,
                    messages: c.messages.map(m =>
                      m.id === placeholderId
                        ? { ...m, content: chunk.text, status: (chunk.done ? 'complete' : 'streaming') as ChatMessage['status'] }
                        : m,
                    ),
                  }
                : c,
            ),
          }));
        },
        attachments: state.pendingErrorContext ? [state.pendingErrorContext] : undefined,
        route: state.currentRoute,
        mode: state.mode,
      });

      // Replace placeholder with final message
      set(s => ({
        conversations: s.conversations.map(c =>
          c.id === activeConversationId
            ? {
                ...c,
                messages: c.messages.map(m =>
                  m.id === placeholderId ? { ...result, id: placeholderId } : m,
                ),
              }
            : c,
        ),
        isStreaming: false,
      }));
    } catch (error) {
      console.error('ChatEngine error:', error);
      // Replace placeholder with error message
      set(s => ({
        conversations: s.conversations.map(c =>
          c.id === activeConversationId
            ? {
                ...c,
                messages: c.messages.map(m =>
                  m.id === placeholderId
                    ? {
                        ...m,
                        content: "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
                        status: 'error' as const,
                      }
                    : m,
                ),
              }
            : c,
        ),
        isStreaming: false,
      }));
    }
  },

  open: () => set({ isOpen: true, isMinimized: false }),
  close: () => set({ isOpen: false, isMinimized: false }),
  minimize: () => set({ isMinimized: true }),
  maximize: () => set({ isMinimized: false }),

  clearConversation: () => {
    const state = get();
    set({
      conversations: state.conversations.filter(c => c.id !== state.activeConversationId),
      activeConversationId: null,
    });
  },

  newConversation: () => {
    set({ activeConversationId: null });
  },

  setMode: (mode) => set({ mode }),
  setRoute: (route) => set({ currentRoute: route }),

  startGuidedFlow: (flowId) => set({ activeGuidedFlow: flowId, activeGuidedFlowStepIndex: 0 }),
  cancelGuidedFlow: () => set({ activeGuidedFlow: null, activeGuidedFlowStepIndex: 0 }),

  injectErrorContext: (context) => {
    set({ pendingErrorContext: context, isOpen: true, isMinimized: false });
  },
}));
